import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

/** Mirrors crates/server/src/notify.rs. */
export type NotificationKind =
  | 'mention'
  | 'reply'
  | 'board_shared'
  | 'team_invite'
  | 'org_invite'
  | 'issue_assigned'
  | 'issue_commented'
  | 'issue_status'
  | 'issue_due'
  | 'issue_mention'
  | 'project_shared'
  | 'page_mention'
  | 'page_comment'
  | 'page_review_requested'
  | 'page_review_decided'
  | 'page_review_done'
  | 'page_review_due'
  | 'space_post';

export interface Actor {
  id: string;
  name: string;
  color: string;
}

export interface NotificationPayload {
  actor?: Actor;
  boardId?: string;
  boardTitle?: string;
  threadId?: string;
  messageId?: string;
  text?: string;
  role?: string;
  teamId?: string;
  teamName?: string;
  orgId?: string | null;
  orgName?: string;
  issueId?: string;
  key?: string;
  title?: string;
  project?: string;
  /** issue_status: the new status and its category. */
  status?: string;
  category?: 'todo' | 'doing' | 'done';
  /** issue_due: the due date, YYYY-MM-DD. */
  due?: string;
  /** project_shared: the tracker project. */
  projectId?: string;
  projectKey?: string;
  projectName?: string;
  /** page_mention, page_comment: the page of the documents. */
  pageId?: string;
  pageTitle?: string;
  /** space_post: the space of the news. */
  spaceId?: string;
  spaceName?: string;
  /** page_review_decided: what the approver decided. */
  decision?: 'approved' | 'changes';
}

export interface AppNotification {
  id: string;
  kind: NotificationKind | string;
  payload: NotificationPayload;
  read: boolean;
  createdAt: number;
}

export interface NotificationPage {
  items: AppNotification[];
  next: string | null;
  unread: number;
}

export type EmailGroup = 'mention' | 'reply' | 'share' | 'invite' | 'issues' | 'reviews' | 'news';
export const EMAIL_GROUPS: EmailGroup[] = ['mention', 'reply', 'issues', 'reviews', 'news', 'share', 'invite'];
export type EmailMode = 'instant' | 'hourly' | 'daily';
export const EMAIL_MODES: EmailMode[] = ['instant', 'hourly', 'daily'];

export interface NotificationPrefs {
  email: Record<EmailGroup, boolean>;
  /** Emails right away, or a digest of what is unread every hour or every morning. */
  emailMode?: EmailMode;
}

/** The bell polls this often (and whenever the window regains focus). */
export const POLL_MS = 30_000;

export const unreadKey = ['notifications', 'unread'] as const;
export const listKey = ['notifications', 'list'] as const;
export const prefsKey = ['notifications', 'prefs'] as const;

export function useUnreadCount(enabled = true) {
  return useQuery({
    queryKey: unreadKey,
    queryFn: () => api.get<{ count: number }>('/notifications/unread').then((r) => r.count),
    enabled,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
}

/** First page plus any pages loaded with "show more". */
export function useNotificationList(open: boolean) {
  return useQuery({
    queryKey: listKey,
    queryFn: () => api.get<NotificationPage>('/notifications?limit=20'),
    enabled: open,
    refetchInterval: open ? POLL_MS : false,
    refetchOnWindowFocus: true,
  });
}

export async function loadMore(qc: QueryClient, page: NotificationPage) {
  if (!page.next) return;
  const more = await api.get<NotificationPage>(`/notifications?limit=20&before=${page.next}`);
  qc.setQueryData<NotificationPage>(listKey, (cur) => (cur ? { items: [...cur.items, ...more.items], next: more.next, unread: more.unread } : more));
}

function setRead(qc: QueryClient, ids: Set<string> | 'all', unread: number) {
  qc.setQueryData<NotificationPage>(listKey, (cur) =>
    cur ? { ...cur, unread, items: cur.items.map((n) => (ids === 'all' || ids.has(n.id) ? { ...n, read: true } : n)) } : cur,
  );
  qc.setQueryData(unreadKey, unread);
}

export async function markRead(qc: QueryClient, n: AppNotification) {
  if (n.read) return;
  const before = qc.getQueryData<number>(unreadKey) ?? 1;
  setRead(qc, new Set([n.id]), Math.max(0, before - 1));
  try {
    const res = await api.post<{ unread: number }>(`/notifications/${n.id}/read`);
    qc.setQueryData(unreadKey, res.unread);
  } catch {
    void qc.invalidateQueries({ queryKey: ['notifications'] });
  }
}

export async function markAllRead(qc: QueryClient) {
  setRead(qc, 'all', 0);
  try {
    await api.post('/notifications/read-all');
  } catch {
    void qc.invalidateQueries({ queryKey: ['notifications'] });
  }
}

/** Where clicking a notification goes. */
export function notificationLink(n: AppNotification): string {
  const p = n.payload;
  switch (n.kind) {
    case 'mention':
    case 'reply':
      return p.boardId ? `/b/${p.boardId}${p.threadId ? `?comment=${encodeURIComponent(p.threadId)}` : ''}` : '/';
    case 'board_shared':
      return p.boardId ? `/b/${p.boardId}` : '/';
    case 'team_invite': {
      const s = new URLSearchParams();
      if (p.orgId) s.set('org', p.orgId);
      if (p.teamId) s.set('team', p.teamId);
      return `/?${s}`;
    }
    case 'org_invite':
      return p.orgId ? `/?org=${p.orgId}` : '/';
    case 'project_shared':
      return p.projectId ? `/t/${p.projectId}` : '/t';
    case 'issue_assigned':
    case 'issue_commented':
    case 'issue_status':
    case 'issue_due':
    case 'issue_mention':
      return p.key ? `/t/i/${encodeURIComponent(p.key)}` : '/t';
    case 'page_mention':
    case 'page_comment':
      return p.pageId ? `/d/${p.pageId}${p.threadId ? `?comment=${encodeURIComponent(p.threadId)}` : ''}` : '/';
    case 'page_review_requested':
    case 'page_review_decided':
    case 'page_review_done':
    case 'page_review_due':
    case 'space_post':
      return p.pageId ? `/d/${p.pageId}` : '/';
    default:
      return '/';
  }
}

export function usePrefs(enabled: boolean) {
  return useQuery({ queryKey: prefsKey, queryFn: () => api.get<NotificationPrefs>('/notifications/prefs'), enabled, staleTime: 60_000 });
}

export function useSetEmailMode() {
  const qc = useQueryClient();
  return async (emailMode: EmailMode) => {
    qc.setQueryData(prefsKey, await api.put<NotificationPrefs>('/notifications/prefs', { emailMode }));
  };
}

/** Notifications in Telegram (crates/server/src/telegram.rs); `available` is false without a bot. */
export interface TelegramState {
  available: boolean;
  bot?: string | null;
  linked?: boolean;
  username?: string | null;
  groups?: Record<EmailGroup, boolean>;
}

const telegramKey = ['notifications', 'telegram'];

export function useTelegram(enabled: boolean) {
  // Refetched on focus: the person comes back from Telegram after pressing «Start».
  return useQuery({ queryKey: telegramKey, queryFn: () => api.get<TelegramState>('/notifications/telegram'), enabled, staleTime: 30_000, refetchOnWindowFocus: 'always' });
}

export function useTelegramActions() {
  const qc = useQueryClient();
  return {
    /** A one-time t.me link that ties the chat with the bot to this account. */
    link: () => api.post<{ url: string }>('/notifications/telegram/link'),
    setGroup: async (group: EmailGroup, on: boolean) => {
      const prev = qc.getQueryData<TelegramState>(telegramKey);
      if (prev?.groups) qc.setQueryData<TelegramState>(telegramKey, { ...prev, groups: { ...prev.groups, [group]: on } });
      try {
        qc.setQueryData(telegramKey, await api.put<TelegramState>('/notifications/telegram', { groups: { [group]: on } }));
      } catch (e) {
        if (prev) qc.setQueryData(telegramKey, prev);
        throw e;
      }
    },
    unlink: async () => qc.setQueryData(telegramKey, await api.del<TelegramState>('/notifications/telegram')),
  };
}

export function useSetPref() {
  const qc = useQueryClient();
  return async (group: EmailGroup, on: boolean) => {
    const prev = qc.getQueryData<NotificationPrefs>(prefsKey);
    if (prev) qc.setQueryData<NotificationPrefs>(prefsKey, { email: { ...prev.email, [group]: on } });
    try {
      qc.setQueryData(prefsKey, await api.put<NotificationPrefs>('/notifications/prefs', { email: { [group]: on } }));
    } catch (e) {
      if (prev) qc.setQueryData(prefsKey, prev);
      throw e;
    }
  };
}

/** Kinds per inbox tab. */
export const INBOX_TABS = {
  all: null,
  unread: null,
  discussions: ['mention', 'reply', 'issue_commented', 'issue_mention', 'page_mention', 'page_comment', 'space_post'],
  issues: ['issue_assigned', 'issue_status', 'issue_due', 'page_review_requested', 'page_review_decided', 'page_review_done', 'page_review_due'],
  access: ['board_shared', 'team_invite', 'org_invite', 'project_shared'],
} as const satisfies Record<string, readonly NotificationKind[] | null>;
export type InboxTab = keyof typeof INBOX_TABS;

/** A due date relative to today: how the reminder words it. */
export function dueWhen(due: string | undefined, today: string): 'overdue' | 'today' | 'tomorrow' | 'later' {
  if (!due) return 'later';
  if (due < today) return 'overdue';
  if (due === today) return 'today';
  const t = new Date(`${today}T00:00`);
  t.setDate(t.getDate() + 1);
  const tomorrow = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  return due === tomorrow ? 'tomorrow' : 'later';
}
